import React, { FC, useContext, useMemo } from 'react';

import { useFormikContext } from 'formik';

import BasicButton from '../../../../../components/buttons/basicButton';
import AampeTooltip from 'components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';

import { EditorContext } from 'context/EditorContext';

import { useSendFormulaTestMessageV2Mutation } from 'hooks/formulas/useSendFormulaTestMessage';

import {
    FormulaTestMessageFormValues,
    FormulaTestMessageRequestParamsV2,
} from 'models/formula.model';

import { TestFormulaSectionType, TestMessageStep } from './constant';
import TestFormulaCSVDownloadButton from './elements/TestFormulaCSVDownloadButton';

interface TestFormulaFooterProps {
    handleClose: () => void;
}

const TestFormulaFooter: FC<TestFormulaFooterProps> = ({ handleClose }) => {
    const { values, setFieldValue } =
        useFormikContext<FormulaTestMessageFormValues>();

    const {
        editorDetails: { formula, alternateMessagesCount },
    } = useContext(EditorContext);
    const testFormula = useSendFormulaTestMessageV2Mutation(formula?.id!);

    const handleBackAction = () => {
        if (values.currentStep === TestMessageStep.AUDIENCE) {
            setFieldValue('currentStep', TestMessageStep.PREVIEW);
        } else {
            handleClose();
        }
    };
    const handleTest = () => {
        const testData: FormulaTestMessageRequestParamsV2 = {
            contact_ids: values.contact_ids,
            ...(values.template_id && { template_id: values.template_id }),
            filters: values.filters,
            message_content_id: values.message_content_id,
            formula_id: values.formula_id,
            integration_attribute_values: values.integration_attribute_values,
        };

        testFormula.mutate(testData, {
            onError: () => {
                setFieldValue('currentSection', TestFormulaSectionType.ERROR);
            },
            onSuccess: ({ data }) => {
                setFieldValue('currentSection', TestFormulaSectionType.SUCCESS);
                setFieldValue('testMessageContent', data.test_message_content);
                setFieldValue('requestId', data.request_id);
            },
        });
    };

    const handleNextButton = () => {
        if (values.currentStep === TestMessageStep.PREVIEW) {
            setFieldValue('currentStep', TestMessageStep.AUDIENCE);
        } else {
            handleTest();
        }
    };

    const backButtonText =
        values.currentStep === TestMessageStep.AUDIENCE
            ? 'Edit message'
            : 'Close';
    const actionButtonText =
        values.currentStep === TestMessageStep.PREVIEW
            ? 'Select test audience'
            : 'Send test message';

    const isDisabled = useMemo<boolean>(() => {
        if (values.currentStep === TestMessageStep.PREVIEW) {
            return values.isNextButtonDisabled || !values.message_content_id;
        } else {
            return !values.contact_ids?.length || !values.message_content_id;
        }
    }, [values]);

    if (values.currentSection !== TestFormulaSectionType.FORM) {
        return null;
    }

    const toolTipTitle = isDisabled
        ? values.currentStep === TestMessageStep.PREVIEW
            ? 'Narrow down to one message to enable audience selection.'
            : 'Please select test audience to proceed'
        : '';

    return (
        <>
            <BasicButton
                id="Back"
                type="button"
                isPrimary={false}
                name={backButtonText}
                onClick={handleBackAction}
            />
            <InfoSection>
                <MessageCountWrapper>
                    <MessageCount>{alternateMessagesCount}</MessageCount>{' '}
                    alternate messages
                </MessageCountWrapper>
                <TestFormulaCSVDownloadButton />
                <AampeTooltip title={toolTipTitle}>
                    <div>
                        <BasicButton
                            id="Action"
                            type="button"
                            name={actionButtonText}
                            isDisabled={isDisabled}
                            onClick={handleNextButton}
                            isLoading={testFormula.isPending}
                        />
                    </div>
                </AampeTooltip>
            </InfoSection>
        </>
    );
};

export default TestFormulaFooter;

const InfoSection = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(3),
});

const MessageCountWrapper = styled.span({});

const MessageCount = styled.span({
    color: aampeTheme.colors.solidA01,
});
